import type { ApiComponentType, Properties } from "@ignite/api"
import request from "@ignite/api/request"

export type MenuResponse = ApiComponentType & {
  url: string
  title: string
  children: MenuResponse[]
  properties?: Properties
}

export type MenuIdentifier = "main" | "footer" | "top"

export type MenusApi = {
  getMenu: (identifier: MenuIdentifier) => Promise<MenuResponse>
  getMenus: () => Promise<MenuResponse[]>
}

/**
 * The API omits `children` for leaf items (e.g. external-link menu items), but
 * the `MenuResponse` type and every consumer assume it is always an array.
 * A missing array throws during render (`item.children.length` in CategoryItem)
 * and, because the menu lives in the global header, takes down the entire app.
 * Normalize here so the declared contract actually holds for all consumers.
 */
export const normalizeMenu = (menu: MenuResponse): MenuResponse => ({
  ...menu,
  children: (menu.children ?? []).map(normalizeMenu),
})

const menusApi: MenusApi = {
  getMenu: async (identifier) =>
    normalizeMenu(await request.get<MenuResponse>(`/menu/${identifier}`)),
  getMenus: async () => {
    const menus =
      window.MENU_DATA || (await request.get<MenuResponse[]>("/menus"))

    return (menus ?? []).map(normalizeMenu)
  },
}

export default menusApi
